// Utility function to check if a response indicates authentication failure
export async function checkAuthError(response) {
  // Check for 403 Forbidden status
  if (response.status === 403) {
    return true;
  }

  // Check if response is HTML (login page) instead of JSON
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('text/html')) {
    return true;
  }

  // Try to peek at the response to see if it's HTML
  try {
    const text = await response.clone().text();
    if (text.trim().startsWith('<!DOCTYPE') || text.includes('<html')) {
      return true;
    }
  } catch (e) {
    // If we can't read the response, assume it's not an auth error
    return false;
  }

  return false;
}

// Handle authentication errors by redirecting to login
export function handleAuthError() {
  const currentPath = window.location.pathname + window.location.search;
  window.location.href = `/accounts/login/?next=${encodeURIComponent(
    currentPath,
  )}`;
}

// Safe JSON parsing with auth error detection
export async function safeJsonParse(response, options = {}) {
  const allowErrorStatus = Boolean(options.allowErrorStatus);

  // Check for 403 Forbidden status first
  if (response.status === 403) {
    handleAuthError();
    throw new Error('Authentication required');
  }

  // Clone the response so we can check content type without consuming it
  const clonedResponse = response.clone();

  // Check content type
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('text/html')) {
    handleAuthError();
    throw new Error('Authentication required - received HTML instead of JSON');
  }

  // Peek at the response text to check if it's HTML
  try {
    const text = await clonedResponse.text();
    if (text.trim().startsWith('<!DOCTYPE') || text.includes('<html')) {
      handleAuthError();
      throw new Error(
        'Authentication required - received HTML instead of JSON',
      );
    }
  } catch (e) {
    // If we can't read the cloned response, continue with original parsing
  }

  // Ensure response is OK before parsing, unless the caller will handle errors
  if (!response.ok && !allowErrorStatus) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  // Try to parse JSON
  try {
    return await response.json();
  } catch (error) {
    // If parsing fails and we haven't already detected HTML, re-throw
    throw error;
  }
}
